import { useEffect } from 'react';
import useAppStore from '../../stores/app';
import useSessionStore from '../../stores/session';

/**
 * DockBadge Component
 * 
 * Manages the desktop app dock badge count based on waiting and reconnect session counts.
 * Only active when running as a desktop app (Electron).
 * 
 * Features:
 * - Updates badge count when waiting or reconnect sessions change
 * - Clears badge when component unmounts
 * - Only runs when isDesktopApp is true
 * 
 * @returns {null} This component doesn't render anything
 */
export function AppNotifications() {
    const isDesktopApp = useAppStore((state) => state.isDesktopApp);
    const waiting = useSessionStore((state) => state.waiting);
    const reconnect = useSessionStore((state) => state.reconnect);

    const waitingCount = Object.keys(waiting).length;
    const reconnectCount = Object.keys(reconnect).length;
    const totalCount = waitingCount + reconnectCount;

    const sendNotification = async (count) => {
        // if this window is also not focused, go ahead and send a notifiation...
        const state = await window.electronAPI.getWindowState();
        if (state && !state.isFocused && count > 0) {
            // Send notification when window is not focused
            try {
                let body = '';
                let title = 'Session Needs Onboarding';

                if (waitingCount > 0 && reconnectCount > 0) {
                    body = `${waitingCount} waiting, ${reconnectCount} reconnecting`;
                    title = 'Sessions Need Onboarding';
                } else if (waitingCount > 0) {
                    body = `${waitingCount} waiting`;
                    title = 'Sessions Waiting';
                } else if (reconnectCount > 0) {
                    body = `${reconnectCount} reconnecting`;
                    title = 'Sessions Reconnecting';
                }

                if (totalCount > 1) {
                    // Don't send notification if both waiting and reconnecting - too noisy
                    title = `Sessions Need Onboarding`;
                } else {
                    title = `Session Needs Onboarding`;
                }

                if (!body) return;

                const result = await window.electronAPI.notify({
                    title: title,
                    body: body,
                    urgency: 'normal'
                });
                console.log('Notification result:', result);
            } catch (error) {
                console.error('Failed to send notification:', error);
            }
        }
    }


    useEffect(() => {
        // Only update badge if running as desktop app
        if (!isDesktopApp) {
            return;
        }

        // Update badge count
        if (totalCount > 0) {
            if (window.badge?.set) {
                window.badge.set(totalCount);
            }
            sendNotification(totalCount);
        } else {
            if (window.badge?.clear) {
                window.badge.clear();
            }
        }
    }, [isDesktopApp, totalCount]);

    // Clear badge on unmount
    useEffect(() => {
        return () => {
            if (isDesktopApp && window.badge?.clear) {
                window.badge.clear();
            }
        };
    }, [isDesktopApp]);

    return null;
}

export default AppNotifications;
